import React, { createContext, useContext, useRef, useCallback, useState, type ReactNode } from 'react';
import { useSettings } from './SettingsContext';

type SoundType =
  | 'startup'
  | 'click'
  | 'error'
  | 'notify'
  | 'shutdown'
  | 'minimize'
  | 'maximize'
  | 'restore'
  | 'close'
  | 'open'
  | 'menuCommand'
  | 'menuPopup'
  | 'navigation'
  | 'logon'
  | 'recycleBin';

interface SoundContextType {
  playSound: (sound: SoundType) => void;
  userHasInteracted: boolean;
  enableSounds: () => void;
}

const SoundContext = createContext<SoundContextType | null>(null);

// Sound file paths (fallback to generated sounds if files don't exist)
const SOUND_URLS: Record<SoundType, string> = {
  startup: '/sounds/startup.mp3',
  click: '/sounds/click.mp3',
  error: '/sounds/error.mp3',
  notify: '/sounds/notify.mp3',
  shutdown: '/sounds/shutdown.mp3',
  minimize: '/sounds/minimize.mp3',
  maximize: '/sounds/maximize.mp3',
  restore: '/sounds/restore.mp3',
  close: '/sounds/close.mp3',
  open: '/sounds/open.mp3',
  menuCommand: '/sounds/menu-command.mp3',
  menuPopup: '/sounds/menu-popup.mp3',
  navigation: '/sounds/navigation.mp3',
  logon: '/sounds/logon.mp3',
  recycleBin: '/sounds/recycle-bin.mp3',
};

interface SoundProviderProps {
  children: ReactNode;
}

export const SoundProvider: React.FC<SoundProviderProps> = ({ children }) => {
  const { settings } = useSettings();
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioBuffersRef = useRef<Map<SoundType, AudioBuffer>>(new Map());
  const loadingRef = useRef<Set<SoundType>>(new Set());

  // Initialize audio context after user interaction
  const enableSounds = useCallback(() => {
    if (!userHasInteracted) {
      setUserHasInteracted(true);
      audioContextRef.current = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    }
  }, [userHasInteracted]);

  // Load audio buffer
  const loadSound = useCallback(async (sound: SoundType): Promise<AudioBuffer | undefined> => {
    if (!audioContextRef.current) return undefined;
    if (audioBuffersRef.current.has(sound)) {
      return audioBuffersRef.current.get(sound)!;
    }
    if (loadingRef.current.has(sound)) return undefined;

    loadingRef.current.add(sound);
    try {
      const response = await fetch(SOUND_URLS[sound]);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
      audioBuffersRef.current.set(sound, audioBuffer);
      loadingRef.current.delete(sound);
      return audioBuffer;
    } catch {
      loadingRef.current.delete(sound);
      // Fallback to generating XP-like sounds
      return generateXPSound(audioContextRef.current, sound);
    }
  }, []);

  // Generate XP-like sounds with multiple oscillators and proper envelopes
  const generateXPSound = (ctx: AudioContext, sound: SoundType): AudioBuffer => {
    const sampleRate = ctx.sampleRate;

    switch (sound) {
      case 'startup':
        return generateStartupSound(ctx, sampleRate);
      case 'error':
        return generateErrorSound(ctx, sampleRate);
      case 'notify':
        return generateNotifySound(ctx, sampleRate);
      case 'shutdown':
        return generateShutdownSound(ctx, sampleRate);
      case 'minimize':
        return generateMinimizeSound(ctx, sampleRate);
      case 'maximize':
      case 'restore':
        return generateMaximizeSound(ctx, sampleRate);
      case 'menuCommand':
        return generateMenuCommandSound(ctx, sampleRate);
      case 'menuPopup':
        return generateMenuPopupSound(ctx, sampleRate);
      case 'navigation':
        return generateNavigationSound(ctx, sampleRate);
      case 'logon':
        return generateLogonSound(ctx, sampleRate);
      case 'recycleBin':
        return generateRecycleBinSound(ctx, sampleRate);
      case 'click':
      case 'close':
      case 'open':
      default:
        return generateClickSound(ctx, sampleRate);
    }
  };

  // XP Startup Sound - Rising chord progression
  const generateStartupSound = (ctx: AudioContext, sampleRate: number): AudioBuffer => {
    const duration = 1.2;
    const buffer = ctx.createBuffer(2, sampleRate * duration, sampleRate);
    const leftData = buffer.getChannelData(0);
    const rightData = buffer.getChannelData(1);

    // Multiple notes for a chord progression
    const notes = [
      { freq: 523.25, start: 0, dur: 0.8 },      // C5
      { freq: 659.25, start: 0.1, dur: 0.7 },    // E5
      { freq: 783.99, start: 0.2, dur: 0.6 },    // G5
      { freq: 1046.50, start: 0.3, dur: 0.5 },   // C6
      { freq: 1318.51, start: 0.5, dur: 0.4 },   // E6
    ];

    for (let i = 0; i < buffer.length; i++) {
      const t = i / sampleRate;
      let sample = 0;

      for (const note of notes) {
        if (t >= note.start && t < note.start + note.dur) {
          const noteT = t - note.start;
          // Attack-decay-sustain-release envelope
          const attack = Math.min(noteT / 0.05, 1);
          const decay = Math.exp(-2 * Math.max(0, noteT - 0.05));
          const envelope = attack * decay * 0.15;
          // Sine wave with slight detuning for richness
          sample += Math.sin(2 * Math.PI * note.freq * noteT) * envelope;
          sample += Math.sin(2 * Math.PI * note.freq * 1.002 * noteT) * envelope * 0.3;
        }
      }

      leftData[i] = sample;
      rightData[i] = sample;
    }

    return buffer;
  };

  // XP Error Sound - Two-tone descending beep
  const generateErrorSound = (ctx: AudioContext, sampleRate: number): AudioBuffer => {
    const duration = 0.5;
    const buffer = ctx.createBuffer(1, sampleRate * duration, sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < buffer.length; i++) {
      const t = i / sampleRate;
      let sample = 0;

      // First beep (higher pitch)
      if (t < 0.15) {
        const envelope = Math.exp(-8 * t) * 0.3;
        sample += Math.sin(2 * Math.PI * 800 * t) * envelope;
        sample += Math.sin(2 * Math.PI * 400 * t) * envelope * 0.5;
      }

      // Second beep (lower pitch)
      if (t >= 0.18 && t < 0.35) {
        const beepT = t - 0.18;
        const envelope = Math.exp(-8 * beepT) * 0.3;
        sample += Math.sin(2 * Math.PI * 600 * beepT) * envelope;
        sample += Math.sin(2 * Math.PI * 300 * beepT) * envelope * 0.5;
      }

      data[i] = sample;
    }

    return buffer;
  };

  // XP Notify Sound - Pleasant ding
  const generateNotifySound = (ctx: AudioContext, sampleRate: number): AudioBuffer => {
    const duration = 0.4;
    const buffer = ctx.createBuffer(2, sampleRate * duration, sampleRate);
    const leftData = buffer.getChannelData(0);
    const rightData = buffer.getChannelData(1);

    for (let i = 0; i < buffer.length; i++) {
      const t = i / sampleRate;

      // Bell-like sound with harmonics
      const fundamental = 1200;
      const attack = Math.min(t / 0.01, 1);
      const decay = Math.exp(-6 * t);
      const envelope = attack * decay * 0.2;

      let sample = Math.sin(2 * Math.PI * fundamental * t) * envelope;
      sample += Math.sin(2 * Math.PI * fundamental * 2 * t) * envelope * 0.3;
      sample += Math.sin(2 * Math.PI * fundamental * 3 * t) * envelope * 0.1;

      leftData[i] = sample;
      rightData[i] = sample;
    }

    return buffer;
  };

  // XP Shutdown Sound - Descending chord
  const generateShutdownSound = (ctx: AudioContext, sampleRate: number): AudioBuffer => {
    const duration = 1.0;
    const buffer = ctx.createBuffer(2, sampleRate * duration, sampleRate);
    const leftData = buffer.getChannelData(0);
    const rightData = buffer.getChannelData(1);

    // Descending notes
    const notes = [
      { freq: 783.99, start: 0, dur: 0.6 },      // G5
      { freq: 659.25, start: 0.15, dur: 0.5 },   // E5
      { freq: 523.25, start: 0.3, dur: 0.5 },    // C5
      { freq: 392.00, start: 0.45, dur: 0.5 },   // G4
    ];

    for (let i = 0; i < buffer.length; i++) {
      const t = i / sampleRate;
      let sample = 0;

      for (const note of notes) {
        if (t >= note.start && t < note.start + note.dur) {
          const noteT = t - note.start;
          const envelope = Math.exp(-3 * noteT) * 0.12;
          sample += Math.sin(2 * Math.PI * note.freq * noteT) * envelope;
          sample += Math.sin(2 * Math.PI * note.freq * 0.5 * noteT) * envelope * 0.3;
        }
      }

      leftData[i] = sample;
      rightData[i] = sample;
    }

    return buffer;
  };

  // XP Click Sound - Short, crisp click
  const generateClickSound = (ctx: AudioContext, sampleRate: number): AudioBuffer => {
    const duration = 0.06;
    const buffer = ctx.createBuffer(1, sampleRate * duration, sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < buffer.length; i++) {
      const t = i / sampleRate;

      // Short click with rapid decay
      const envelope = Math.exp(-60 * t) * 0.25;
      const sample = Math.sin(2 * Math.PI * 2000 * t) * envelope +
                    Math.sin(2 * Math.PI * 800 * t) * envelope * 0.5;

      data[i] = sample;
    }

    return buffer;
  };

  // XP Minimize Sound - Descending whoosh
  const generateMinimizeSound = (ctx: AudioContext, sampleRate: number): AudioBuffer => {
    const duration = 0.15;
    const buffer = ctx.createBuffer(1, sampleRate * duration, sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < buffer.length; i++) {
      const t = i / sampleRate;
      // Descending frequency sweep
      const freq = 800 - (t / duration) * 500;
      const envelope = Math.exp(-8 * t) * 0.2;
      // Add some noise for "whoosh" effect
      const noise = (Math.random() - 0.5) * 0.03 * envelope;
      data[i] = Math.sin(2 * Math.PI * freq * t) * envelope + noise;
    }

    return buffer;
  };

  // XP Maximize Sound - Ascending whoosh
  const generateMaximizeSound = (ctx: AudioContext, sampleRate: number): AudioBuffer => {
    const duration = 0.15;
    const buffer = ctx.createBuffer(1, sampleRate * duration, sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < buffer.length; i++) {
      const t = i / sampleRate;
      // Ascending frequency sweep
      const freq = 400 + (t / duration) * 400;
      const envelope = Math.exp(-6 * t) * 0.2;
      const noise = (Math.random() - 0.5) * 0.02 * envelope;
      data[i] = Math.sin(2 * Math.PI * freq * t) * envelope + noise;
    }

    return buffer;
  };

  // XP Menu Command Sound - Subtle click
  const generateMenuCommandSound = (ctx: AudioContext, sampleRate: number): AudioBuffer => {
    const duration = 0.04;
    const buffer = ctx.createBuffer(1, sampleRate * duration, sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < buffer.length; i++) {
      const t = i / sampleRate;
      const envelope = Math.exp(-80 * t) * 0.15;
      data[i] = Math.sin(2 * Math.PI * 1500 * t) * envelope;
    }

    return buffer;
  };

  // XP Menu Popup Sound - Light ascending blip
  const generateMenuPopupSound = (ctx: AudioContext, sampleRate: number): AudioBuffer => {
    const duration = 0.08;
    const buffer = ctx.createBuffer(1, sampleRate * duration, sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < buffer.length; i++) {
      const t = i / sampleRate;
      const freq = 600 + (t / duration) * 200;
      const envelope = Math.exp(-30 * t) * 0.12;
      data[i] = Math.sin(2 * Math.PI * freq * t) * envelope;
    }

    return buffer;
  };

  // XP Navigation Sound - Page loading click
  const generateNavigationSound = (ctx: AudioContext, sampleRate: number): AudioBuffer => {
    const duration = 0.1;
    const buffer = ctx.createBuffer(1, sampleRate * duration, sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < buffer.length; i++) {
      const t = i / sampleRate;
      // Two-tone click
      let sample = 0;
      if (t < 0.03) {
        const envelope = Math.exp(-50 * t) * 0.2;
        sample = Math.sin(2 * Math.PI * 1800 * t) * envelope;
      }
      if (t >= 0.03 && t < 0.08) {
        const t2 = t - 0.03;
        const envelope = Math.exp(-40 * t2) * 0.15;
        sample = Math.sin(2 * Math.PI * 1200 * t2) * envelope;
      }
      data[i] = sample;
    }

    return buffer;
  };

  // XP Logon Sound - Welcoming chord
  const generateLogonSound = (ctx: AudioContext, sampleRate: number): AudioBuffer => {
    const duration = 0.8;
    const buffer = ctx.createBuffer(2, sampleRate * duration, sampleRate);
    const leftData = buffer.getChannelData(0);
    const rightData = buffer.getChannelData(1);

    // Pleasant welcoming chord
    const notes = [
      { freq: 392.00, start: 0, dur: 0.6 },     // G4
      { freq: 493.88, start: 0.1, dur: 0.5 },   // B4
      { freq: 587.33, start: 0.2, dur: 0.5 },   // D5
    ];

    for (let i = 0; i < buffer.length; i++) {
      const t = i / sampleRate;
      let sample = 0;

      for (const note of notes) {
        if (t >= note.start && t < note.start + note.dur) {
          const noteT = t - note.start;
          const attack = Math.min(noteT / 0.03, 1);
          const decay = Math.exp(-3 * noteT);
          const envelope = attack * decay * 0.12;
          sample += Math.sin(2 * Math.PI * note.freq * noteT) * envelope;
        }
      }

      leftData[i] = sample;
      rightData[i] = sample;
    }

    return buffer;
  };

  // XP Recycle Bin Sound - Crinkle/paper sound
  const generateRecycleBinSound = (ctx: AudioContext, sampleRate: number): AudioBuffer => {
    const duration = 0.4;
    const buffer = ctx.createBuffer(1, sampleRate * duration, sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < buffer.length; i++) {
      const t = i / sampleRate;
      // Crinkly noise that fades out
      const envelope = Math.exp(-6 * t) * 0.25;
      // Filtered noise for paper-like sound
      const noise = (Math.random() - 0.5);
      // Add some metallic resonance
      const resonance = Math.sin(2 * Math.PI * 3000 * t) * 0.1 +
                       Math.sin(2 * Math.PI * 4500 * t) * 0.05;
      data[i] = (noise * 0.8 + resonance) * envelope;
    }

    return buffer;
  };

  // Play a sound
  const playSound = useCallback(async (sound: SoundType) => {
    if (!settings.soundEnabled || !userHasInteracted || !audioContextRef.current) {
      return;
    }

    try {
      // Resume context if suspended
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      let buffer = audioBuffersRef.current.get(sound);
      if (!buffer) {
        buffer = await loadSound(sound);
      }
      if (!buffer) return;

      const source = audioContextRef.current.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContextRef.current.destination);
      source.start(0);
    } catch {
      // Ignore sound errors
    }
  }, [settings.soundEnabled, userHasInteracted, loadSound]);

  return (
    <SoundContext.Provider
      value={{
        playSound,
        userHasInteracted,
        enableSounds,
      }}
    >
      {children}
    </SoundContext.Provider>
  );
};

export const useSound = (): SoundContextType => {
  const context = useContext(SoundContext);
  if (!context) {
    throw new Error('useSound must be used within a SoundProvider');
  }
  return context;
};
